<template>
  <b-card class="card__user-submission-status w-100">
    <div class="submission-header">
      <!-- left -->
      <div class="card-item-1">
        <h3 class="submission-header__title font-weight-bold">Evidence Submission</h3>
        <div class="submission-header__content">
          <h5 class="submission-learning-unit-name mb-3">{{ learningUnit.name }}</h5>
          <div class="submission-status-section">
            <b-badge
              ref="badge__submission-status"
              pill
              :variant="badgeStatusVariant"
              :class="badgeStatusClass"
            >
              <span class="text__status">{{ badgeStatusText }}</span>
            </b-badge>
            <div class="submission-date-selector">
              <span class="submission-date-selector__label">
                {{ isProjectEnded ? 'Project ended:' : 'Submitted Date:' }}
              </span>
              <base-dropdown-select
                ref="select__submissions"
                v-model="$_selectedEvidenceSubmissionId"
                :options="selectOptions"
                value-field="value"
                text-field="text"
                :disabled="isDeniedPage"
                placeholder="Latest Submission"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- right -->
      <div class="card-item-2">
        <div class="submission-header__id">
          <span> Submission ID: </span>
          <span ref="text__code" class="submission-header__code font-weight-bold">
            {{ selectedEvidenceSubmission.code }}
          </span>
        </div>
        <a
          v-if="selectedEvidenceSubmission.isAwarded"
          role="button"
          class="button__share-submission btn btn-outline-primary"
          :href="shareBadgeUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="currentColor"
          >
            <path
              d="M13.1202 17.0228L8.92129 14.7324C8.19135 15.5125 7.15261 16 6 16C3.79086 16 2 14.2091 2 12C2 9.79086 3.79086 8 6 8C7.15255 8 8.19125 8.48746 8.92118 9.26746L13.1202 6.97713C13.0417 6.66441 13 6.33707 13 6C13 3.79086 14.7909 2 17 2C19.2091 2 21 3.79086 21 6C21 8.20914 19.2091 10 17 10C15.8474 10 14.8087 9.51251 14.0787 8.73246L9.87977 11.0228C9.9583 11.3355 10 11.6629 10 12C10 12.3371 9.95831 12.6644 9.87981 12.9771L14.0788 15.2675C14.8087 14.4875 15.8474 14 17 14C19.2091 14 21 15.7909 21 18C21 20.2091 19.2091 22 17 22C14.7909 22 13 20.2091 13 18C13 17.6629 13.0417 17.3355 13.1202 17.0228ZM6 14C7.10457 14 8 13.1046 8 12C8 10.8954 7.10457 10 6 10C4.89543 10 4 10.8954 4 12C4 13.1046 4.89543 14 6 14ZM17 8C18.1046 8 19 7.10457 19 6C19 4.89543 18.1046 4 17 4C15.8954 4 15 4.89543 15 6C15 7.10457 15.8954 8 17 8ZM17 20C18.1046 20 19 19.1046 19 18C19 16.8954 18.1046 16 17 16C15.8954 16 15 16.8954 15 18C15 19.1046 15.8954 20 17 20Z"
            ></path>
          </svg>
          <span>Share</span>
        </a>
      </div>
    </div>

    <div class="submission-alerts">
      <div
        v-if="
          selectedEvidenceSubmission.isNotSubmitted && !selectedEvidenceSubmission.isFirstSubmission
        "
        class="submission-feedback"
      >
        Please revise all your evidence again based on the feedback from assessor.
      </div>

      <AlertSubmitEvidence
        :show-alert="selectedEvidenceSubmission.isDenied && !isProjectEnded"
        :alert-type="'denied'"
        :title="'Submission Denied'"
        :description="'Some parts need improvement. Please review the feedback from assessor on your evidence.'"
      />

      <AlertSubmitEvidence
        :show-alert="isProjectEnded"
        :alert-type="'warning'"
        :title="'Project period has ended'"
        :description="'No submission quota left. Purchase more to submit your evidence again.'"
      />

      <AlertSubmitEvidence
        :show-alert="selectedEvidenceSubmission.isReviewPending"
        :alert-type="'info'"
        :title="'Submission has been sent successfully.'"
        :description="'The review takes at least 14 days. A notification email will be sent after it is completed.'"
      />
    </div>

    <EvidenceSubmissionStepsGuide v-if="showBanner" />

    <div
      v-if="selectedEvidenceSubmission.isAwarded && assessorRecommendation"
      class="border-top pt-u5"
    >
      <div class="font-weight-bold mb-1 title__recommendation mb-3">Assessor's recommendation</div>
      <div
        v-safe-html="assessorRecommendation"
        class="border border-medium rounded p-3 text-pre-wrap text__recommendation bg-secondary"
      ></div>
    </div>

    <b-tooltip
      target="quota-info-tooltip"
      placement="bottom"
      triggers="hover"
      class="quota-tooltip"
      variant="light"
      custom-class="quota-tooltip--custom"
    >
      Quota is used for every submission. Once it runs out, you'll need to make a purchase to
      continue submitting.
    </b-tooltip>
  </b-card>
</template>

<script>
import { orderBy } from 'lodash';
import User from '@/models/User';
import LearningUnit from '@/models/LearningUnit';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import BaseDropdownSelect from '../base-components/BaseDropdownSelect';
import AlertSubmitEvidence from '@/views/EvidenceSubmission/components/AlertSubmitEvidence.vue';
import { useDateTimeFormat } from '@/composables/useDateTimeFormat';
import EvidenceSubmissionStepsGuide from './EvidenceSubmissionStepsGuide.vue';

export default {
  components: {
    BaseDropdownSelect,
    AlertSubmitEvidence,
    EvidenceSubmissionStepsGuide
  },

  model: {
    prop: 'selectedEvidenceSubmissionId',
    event: 'change'
  },
  props: {
    user: {
      type: User,
      required: true
    },

    learningUnit: {
      type: LearningUnit,
      required: true
    },

    selectedEvidenceSubmissionId: {
      type: [Number, String],
      required: true
    },

    view: {
      type: String,
      default: ''
    },

    assessorRecommendation: {
      type: String,
      default: null
    }
  },

  computed: {
    $_selectedEvidenceSubmissionId: {
      get() {
        return this.selectedEvidenceSubmissionId;
      },

      set(value) {
        this.$emit('change', value);
      }
    },

    evidenceSubmissions() {
      const evidenceSubmissions = EvidenceSubmission.query()
        .where('learning_unit_id', this.learningUnit.id)
        .where('user_id', this.user.id)
        .get();

      return orderBy(evidenceSubmissions, ['created_at'], 'desc');
    },

    selectOptions() {
      const { formatedDatetime } = useDateTimeFormat();
      return (
        this.evidenceSubmissions
          // Exclude project-ended submissions (DENIED with no assessor review,
          // i.e. no submitted date) from the dropdown options — except the one
          // currently selected, so its date still renders instead of falling
          // back to the "Latest Submission" placeholder.
          .filter(
            (submission) =>
              !this.checkProjectEnded(submission) ||
              submission.id == this.$_selectedEvidenceSubmissionId
          )
          .map((submission) => {
            const { id, submitted_at, updated_at } = submission;
            let text = 'Latest Submission';

            if (submitted_at) {
              text = formatedDatetime(submitted_at, 'MMM D, YYYY hh:mm a ');
            } else if (this.checkProjectEnded(submission)) {
              // Project ended without a submission: use updated_at as the end
              // date (date only, no time).
              text = formatedDatetime(updated_at, 'MMM D, YYYY');
            }

            return {
              value: id,
              text
            };
          })
      );
    },

    selectedEvidenceSubmission() {
      return EvidenceSubmission.find(this.$_selectedEvidenceSubmissionId);
    },

    evidenceSubmissionStatus() {
      return this.selectedEvidenceSubmission.status;
    },

    isProjectEnded() {
      return this.checkProjectEnded(this.selectedEvidenceSubmission);
    },

    badgeStatusText() {
      return this.isProjectEnded ? 'Project ended' : this.readableStatus;
    },

    badgeStatusVariant() {
      if (this.isProjectEnded) return 'neutral-warning';

      let variant;

      switch (this.evidenceSubmissionStatus) {
        case 'REVIEW_PENDING':
          variant = 'neutral-info';
          break;

        case 'DENIED':
          variant = 'neutral-danger';
          break;

        case 'AWARDED':
          variant = 'neutral-success';
          break;

        default:
          variant = 'light';
          break;
      }

      return variant;
    },

    badgeStatusClass() {
      return {
        'badge__evidence-submission-not-submitted-status text-dark':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.NotSubmitted,
        'badge__evidence-submission-review-pending-status text-info':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.ReviewPending,
        'badge__evidence-submission-denied-status text-danger':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.Denied &&
          !this.isProjectEnded,
        'badge__evidence-submission-awarded-status text-success':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.Awarded,
        'badge__evidence-submission-project-ended-status text-warning': this.isProjectEnded
      };
    },

    readableStatus() {
      let status = this.selectedEvidenceSubmission.readableStatus;
      if (
        this.view === 'learner' &&
        (status === 'In Progress by Assessor' || status === 'In Progress by Issuer')
      ) {
        status = EvidenceSubmission.STATUS.REVIEW_PENDING;
      }

      if (this.view === 'learner' && status === 'Awarded') {
        status = EvidenceSubmission.STATUS.ACHIEVED;
      }

      return status;
    },

    countRemainingSubmission() {
      if (!this.isApplied) return 0;
      const notSubmittedCount = this.evidenceSubmissions.filter(
        (submission) => submission.isNotSubmitted
      ).length;
      return this.isApplied.remaining_submission + notSubmittedCount;
    },

    isDeniedPage() {
      return this.$route.name === 'learning-unit/submission-denied';
    },

    showBanner() {
      return (
        this.selectedEvidenceSubmission.isFirstSubmission &&
        this.selectedEvidenceSubmission.isNotSubmitted
      );
    },

    shareBadgeUrl() {
      const baseUrl = process.env.VUE_APP_CATALOG_APP_URL;
      const learningUnitId = this.selectedEvidenceSubmission.learning_unit_id;

      return `${baseUrl}/badge-collection?learning_unit_id=${learningUnitId}`;
    }
  },
  methods: {
    checkProjectEnded(evidenceSubmission) {
      // A denied submission without a submitted date means the project period
      // ended before the learner could submit their evidence.
      return Boolean(
        evidenceSubmission && evidenceSubmission.isDenied && !evidenceSubmission.submitted_at
      );
    }
  }
};
</script>

<style scoped lang="scss">
/* base = mobile */
.card__user-submission-status {
  .submission-header {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 16px;
  }

  .submission-header {
    &__title {
      margin-bottom: 4px;
    }

    &__id {
      font-size: 14px;
      color: #7a7b97;
    }
  }

  .submission-learning-unit-name {
    font-weight: 400;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 28px;
  }

  .submission-status-section {
    font-size: 14px;
    display: flex;
    gap: 16px;
    flex-direction: column;

    .badge {
      width: fit-content;
    }

    .text__status {
      font-size: 14px;
    }
  }

  .submission-quota,
  .submission-date-selector {
    border-left: 2px solid #d1d2db;
    padding-left: 16px;
    display: flex;
    align-items: center;
  }

  .submission-date-selector__select {
    cursor: pointer;
  }

  .submission-date-selector {
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;

    &__label {
      font-size: 14px;
      color: #7a7b97;
    }
  }

  .submission-feedback {
    border-top: 1px solid #d1d2db;
    padding-top: 20px;
    display: flex;
    flex-direction: row;
    gap: 20px;
    color: #7a7b97;
  }

  .quota-tooltip {
    min-width: 320px;
    background: transparent;
    cursor: pointer;
  }

  .submission-header__id {
    margin-bottom: 16px;
  }

  .button__share-submission {
    width: 100%;

    svg {
      margin-right: 8px;
    }
  }

  /* tablet */
  @media screen and (min-width: 768px) {
    .card-item-1 {
      flex: 1;
    }

    .submission-status-section {
      flex-direction: row;
    }
  }

  /* desktop */
  @media screen and (min-width: 992px) {
    .submission-status-section {
      flex-direction: row;
    }
  }
}

.quota-tooltip--custom {
  ::v-deep .arrow::before {
    border-bottom-color: #ffffff !important;
  }

  ::v-deep .tooltip-inner {
    max-width: 260px;
    text-align: left;
    background: #ffffff;
    color: #2d3436;
    opacity: 1;
  }
}

.quota-tooltip--custom::v-deep.tooltip.b-tooltip {
  opacity: 1;
}
</style>
